import { Button, Popover, StatusIndicator } from "@amzn/awsui-components-react";
import { Order } from "@amzn/ito-client";
import { useFlashBarItemsActions } from "common/UseFlashBarItems/useFlashBarItems";
import { useEffect, useState } from "react";

import { isCancellationEligible, isTerminalStatus, getHopOrdersFromItems } from "../../../../helpers/cancellation-helpers";
import { useCancelOrder } from "../../../../hooks/useCancelOrder";
import { CSRFService } from "../../../../services/csrf-service";
import { CancelOrderModal } from "./CancelOrderModal";

export interface CancelOrderButtonProps {
    order: Order;
    amznhamOrder?: Order;
    flashBarItemsActions: useFlashBarItemsActions;
    onCancellationComplete: () => void;
}

export const CancelOrderButton = ({ order, amznhamOrder, flashBarItemsActions, onCancellationComplete }: CancelOrderButtonProps) => {
    const [modalVisible, setModalVisible] = useState(false);
    const { isSubmitting, error, cancelOrder, data } = useCancelOrder();

    const isTerminal = isTerminalStatus(order.status ?? "");

    useEffect(() => {
        if (data && !error) {
            flashBarItemsActions.addSuccessFlashBarItem("Cancellation has been initiated successfully.");
            setModalVisible(false);
            onCancellationComplete();
        }
    }, [data]);

    useEffect(() => {
        if (error) {
            flashBarItemsActions.addErrorFlashBarItem(error.message);
            setModalVisible(false);
        }
    }, [error]);

    const handleClick = () => {
        const orderForHopCheck = amznhamOrder ?? order;
        const hopOrders = getHopOrdersFromItems(orderForHopCheck);
        if (!isCancellationEligible(hopOrders)) {
            flashBarItemsActions.addErrorFlashBarItem(
                "Order cannot be cancelled — HOP order has progressed beyond Ready for Export."
            );
            return;
        }
        setModalVisible(true);
    };

    const handleConfirm = async (reason: string) => {
        const csrfService = new CSRFService();
        const token = await csrfService.getToken();
        cancelOrder(order, reason, token, amznhamOrder);
    };

    const button = (
        <Button
            data-testid="cancel-order-button"
            variant="normal"
            disabled={isTerminal}
            onClick={handleClick}
        >
            Cancel Order
        </Button>
    );

    return (
        <>
            {isTerminal ? (
                <Popover
                    dismissButton={false}
                    position="top"
                    size="medium"
                    triggerType="custom"
                    content={
                        <StatusIndicator type="info">
                            This order cannot be cancelled because it is in a terminal status.
                        </StatusIndicator>
                    }
                >
                    <span style={{ display: "inline-block" }}>
                        <span style={{ pointerEvents: "none" }}>
                            <Button
                                data-testid="cancel-order-button"
                                variant="normal"
                                disabled
                            >
                                Cancel Order
                            </Button>
                        </span>
                    </span>
                </Popover>
            ) : (
                button
            )}
            <CancelOrderModal
                visible={modalVisible}
                order={order}
                onDismiss={() => setModalVisible(false)}
                onConfirm={handleConfirm}
                isSubmitting={isSubmitting}
            />
        </>
    );
};
